import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import './App.css';
import Bubble from './bubbles';
import BackgroundImage from './background';
import HomepageHeader from './homepageHeader';
import Content from './contentPlane';
import Services from './services';
import ServiceShowcase from './serviceShowcase';
import ServiceSplitSlider from './serviceSplitSlider';
import Footer from './footer';
import SideNavBar from './sidebar';
import Form from './contactForm';
import logo from './assets/nobg.png';
import gradientImage from './assets/dark-purple-modern-bokeh-pattern-vector.jpg';
import demos from './assets/demo-removebg-preview.png';
import reports from './assets/management-report.png';
import advice from './assets/advice.png';
import merchandising from './assets/merchandising-removebg-preview.png';
import whatwedo from './assets/whatwedo-removebg-preview.png';
import ourphilosophy from './assets/ourphilosophy-new.png';
import coveragearea from './assets/coveragearea.svg';
import english from './assets/en.gif';
import spanish from './assets/es.jpg';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';

const SuccessStories = lazy(() => import('./successStories'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const Messages = lazy(() => import('./pages/Messages'));

function ScrollToTop() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    if (!state?.scrollTo) {
      window.scrollTo(0, 0);
    }
  }, [pathname, state]);
  return null;
}

function ScrollToState() {
  const { state } = useLocation();
  useEffect(() => {
    if (state?.scrollTo) {
      window.scrollTo(0, 0);
      const id = state.scrollTo;
      const attempt = (tries) => {
        const el = document.getElementById(id);
        if (el) {
          const navbarHeight = document.querySelector('.navbar-container')?.offsetHeight || 0;
          // Walk offsetParent chain to get true layout position, unaffected by CSS transforms
          let top = 0;
          let node = el;
          while (node) {
            top += node.offsetTop;
            node = node.offsetParent;
          }
          window.scrollTo({ top: top - navbarHeight, behavior: 'smooth' });
        } else if (tries > 0) {
          setTimeout(() => attempt(tries - 1), 150);
        }
      };
      setTimeout(() => attempt(10), 600);
    }
  }, [state]);
  return null;
}

function HomeAnimations() {
  useEffect(() => {
    let observer;
    const timer = setTimeout(() => {
      const els = document.querySelectorAll('.main-animate');
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) entry.target.classList.add('main-visible');
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
      );
      els.forEach(el => observer.observe(el));
    }, 400);
    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, []);
  return null;
}

function App() {
  const [showNavbar, setShowNavbar] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);


  const toggleDropdown = (e) => {
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-dropdown')) {
        setDropdownOpen(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);




  useEffect(() => {
    // Make the navbar appear after a short delay
    setTimeout(() => setShowNavbar(true), 200);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);

    const handleScroll = () => {
      setShowNavbar(true); // Always keep navbar visible
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [menuOpen, setMenuOpen] = useState(false);
  const navbarRef = useRef(null);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to scroll to the footer section
  const scrollToContact = () => {
    const contentContainer = document.querySelector('.contact-form');
    const navbarHeight = document.querySelector('.navbar-container').offsetHeight;

    window.scrollTo({
      top: contentContainer.offsetTop,
      behavior: 'smooth',
    });
  };

  const [language, setLanguage] = useState('en');

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  const bubblesData = [
    {
      id: 2,
      icon: whatwedo,
      title: language === 'en' ? 'What We Do' : 'Qué Hacemos',
      content:
        language === 'en'
          ? [
              'We focus on developing your brand, products and market for more effective sales',
              'We specialize in topics such as: habits, culture, generational trends, and competition',
              'Our structure allows our clients to pay a fraction of the cost in merchandising, allowing you to excel your business at a fraction of the price',
            ]
          : [
              'Nos enfocamos en desarrollar su marca, productos y mercado para ventas más efectivas.',
              'Nos especializamos en temas como: hábitos, cultura, tendencias generacionales y competencia.',
              'Nuestra estructura permite a nuestros clientes pagar una fracción del costo en comercialización, permitiéndole sobresalir en su negocio a una fracción del precio.',
            ],
    },
    {
      id: 3,
      icon: ourphilosophy,
      title: language === 'en' ? 'Our Philosophy' : 'Nuestra Filosofía',
      content:
        language === 'en'
          ? [
              'Be a professional and reliable contact in the demo and merchandising market for our clients',
              'Offer reliable, simple, constant information that allows efficient and reliable decision making for our clients',
              'Grow with our clients, offering them the best service and the best results in the industry',
            ]
          : [
              'Ser un contacto profesional y confiable en el mercado de demostración y comercialización para nuestros clientes.',
              'Ofrecer información confiable, sencilla y constante que permita la toma de decisiones eficiente y confiable para nuestros clientes.',
              'Crecer con nuestros clientes, ofreciéndoles el mejor servicio y los mejores resultados de la industria.',
            ],
    },
    {
      id: 4,
      icon: coveragearea,
      title: language === 'en' ? 'Our Coverage Area' : 'Área de Cobertura',
      content:
        language === 'en'
          ? [
              'We have coverage in all supermarkets in the San Francisco Bay Area, as well as locations in Los Angeles, San Diego, El Centro, and Las Vegas.',
            ]
          : [
              'Tenemos cobertura en todos los supermercados del área de la Bahía de San Francisco, así como en ubicaciones en Los Ángeles, San Diego, El Centro y Las Vegas.',
            ],
    },
  ];

  const contentItems = [
    {
      title: language === 'en' ? 'Merchandising' : 'Comercialización',
      icon: merchandising, // Replace with actual icon URL
      description:
        language === 'en'
          ? 'We will handle your merchandising needs including: positioning, pricing, inventory, exhibits, purchase orders, confirmation of sold product, reset of shelves and display points'
          : 'Nos encargaremos de sus necesidades de comercialización, incluyendo: posicionamiento, precios, inventario, exhibiciones, órdenes de compra, confirmación de producto vendido, reajuste de estantes y puntos de exhibición.',
    },
    {
      title: language === 'en' ? 'Special Event Demos' : 'Demostraciones de Eventos Especiales',
      icon: demos, // Replace with actual icon URL
      description:
        language === 'en'
          ? 'We provide the following reports for special event demos: floor inventories, customer feedback, comments from stores about product'
          : 'Proporcionamos los siguientes informes para demostraciones de eventos especiales: inventarios de piso, comentarios de clientes, comentarios de las tiendas sobre el producto.',
    },
    {
      title: language === 'en' ? 'Management Reports' : 'Informes de Gestión',
      icon: reports, // Replace with actual icon URL
      description:
        language === 'en'
          ? 'Management reports in the following areas: increasing visibility of your products, sales analytics, review of your brand, product, and market'
          : 'Informes de gestión en las siguientes áreas: aumento de la visibilidad de sus productos, análisis de ventas, revisión de su marca, producto y mercado.',
    },

    {
      title: language === 'en' ? 'Professional Advice' : 'Asesoramiento Profesional',
      icon: advice, // Replace with actual icon URL
      description:
        language === 'en'
          ? 'We provide professional advice on the following topics: marketing imagery, commercial trends, web page development, social media strategies'
          : 'Brindamos asesoría profesional en los siguientes temas: imagen de marketing, tendencias comerciales, desarrollo de páginas web, estrategias de redes sociales.',
    },
  ];

  const companyInfo = {
    eyebrow: language === 'en' ? 'Your partner in product growth' : 'Tu socio en el crecimiento de tu producto',
    heading:
      language === 'en'
        ? 'See how ProTarget can level up your product'
        : 'Descubre cómo ProTarget puede llevar tu producto al siguiente nivel',
    description:
      language === 'en'
        ? `Illuminating your products to attract attention, engage customers, and create lasting connections. We design displays that not only highlight your product's best features but also foster meaningful interactions with your customers. Whether it's an eye-catching retail display or an interactive demo, we help bridge the gap between your products and your audience.`
        : `Iluminamos sus productos para atraer la atención, captar clientes y crear conexiones duraderas. Diseñamos exhibiciones que no solo resaltan las mejores características de su producto, sino que también fomentan interacciones significativas con sus clientes. Ya sea una exhibición llamativa en tienda o una demostración interactiva, ayudamos a conectar sus productos con su audiencia.`,
    btnServices: language === 'en' ? 'Explore Services' : 'Explorar Servicios',
    btnLearn: language === 'en' ? 'Learn More' : 'Más Información',
  };

  const contactSection = {
    title: language === 'en' ? 'Contact Us' : 'Contáctenos',
    home: language === 'en' ? 'Home' : 'Inicio',
    about: language === 'en' ? 'About Us' : 'Sobre nosotros',
    services: language === 'en' ? 'Services' : 'Servicios',
    expect: language === 'en' ? 'What You Can Expect' : 'Lo que puede esperar',
    whyChoose: language === 'en' ? 'Why Choose ProTarget' : '¿Por qué elegir ProTarget?',
    customers: language === 'en' ? 'Our Customers' : 'Nuestros Clientes',
    contact: language === 'en' ? 'Contact Us' : 'Contáctenos',
    links: language === 'en' ? 'Quick Links' : 'Enlaces rápidos',
    email: language === 'en' ? 'Email' : 'Correo electrónico',
    phone: language === 'en' ? 'Phone' : 'Teléfono',
    address: language === 'en' ? 'Address' : 'Dirección',
    rights: language === 'en' ? 'All rights reserved' : 'Reservados todos los derechos',
  };

  const formInfo = {
    title: language === 'en' ? "Let's Work Together" : 'Trabajemos juntos',
    name: language === 'en' ? 'Your Name / Company Name' : 'Su nombre / Nombre de la empresa',
    contact: language === 'en' ? 'Contact Info' : 'Información de contacto',
    message: language === 'en' ? 'Your Message' : 'Tu mensaje',
    send: language === 'en' ? 'Send Message' : 'Enviar mensaje',
    ctaSub: language === 'en'
      ? "Ready to grow your brand's presence? Let's put your products in front of the right customers in every aisle, every season."
      : '¿Listo para hacer crecer la presencia de su marca? Pongamos sus productos frente a los clientes correctos en cada pasillo y cada temporada.',
    contactBtn: language === 'en' ? 'Contact Us' : 'Contáctenos',
  };

  const customerTitle =
    language === 'en' ? 'Some Of Our Happy Customers' : 'Algunos de nuestros clientes satisfechos';

  const sections = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'services', label: 'Services' },
    { id: 'customers', label: 'Customers' },
    { id: 'contact-form', label: 'Contact' },
  ];

  // make to spanish
  const dataPoints = [
    { 
      value: language === 'en' ? "4x" : "4x",
      label: language === 'en' ? "Increase in Product Visibility" : "Aumento de la Visibilidad del Producto",
    },
    { 
      value: language === 'en' ? "350+" : "350+",
      label: language === 'en' ? "Stores Served Monthly" : "Tiendas Atendidas Mensualmente",
    },
    { 
      value: language === 'en' ? "12+" : "12+",
      label: language === 'en' ? "Years of Experience" : "Años de Experiencia",
    },
  ];

  // make to spanish
  const servicesTitle = language === 'en' ? "Why Choose ProTarget?" : "¿Por qué elegir ProTarget?";

  const showcaseServices = [
    {
      title: language === 'en' ? 'Special Event Demos' : 'Demostraciones de Eventos Especiales',
      icon: demos,
      description:
        language === 'en'
          ? 'We execute in-store product demonstrations that connect your brand directly with consumers at the point of sale.'
          : 'Ejecutamos demostraciones de productos en tienda que conectan su marca directamente con los consumidores en el punto de venta.',
      bullets:
        language === 'en'
          ? [
              'Floor inventory tracking',
              'Real-time customer feedback collection',
              'Store staff communication & coordination',
              'Post-event reporting with actionable insights',
              'Bilingual demo staff available',
            ]
          : [
              'Seguimiento de inventario en piso',
              'Recopilación de comentarios de clientes en tiempo real',
              'Comunicación y coordinación con el personal de la tienda',
              'Informes post-evento con información útil',
              'Personal de demostración bilingüe disponible',
            ],
      hintLabel: language === 'en' ? 'Click to explore' : 'Haz clic para explorar',
    },
    {
      title: language === 'en' ? 'Merchandising' : 'Comercialización',
      icon: merchandising,
      description:
        language === 'en'
          ? 'We manage every aspect of your product\'s presence on the shelf so your brand always looks its best.'
          : 'Gestionamos cada aspecto de la presencia de su producto en el estante para que su marca siempre luzca en su mejor momento.',
      bullets:
        language === 'en'
          ? [
              'Product positioning & pricing compliance',
              'Inventory audits & replenishment',
              'Display & exhibit setup',
              'Purchase order confirmation',
              'Shelf resets & display point maintenance',
            ]
          : [
              'Posicionamiento de productos y cumplimiento de precios',
              'Auditorías de inventario y reabastecimiento',
              'Configuración de exhibidores y stands',
              'Confirmación de órdenes de compra',
              'Reajuste de estantes y mantenimiento de puntos de exhibición',
            ],
      hintLabel: language === 'en' ? 'Click to explore' : 'Haz clic para explorar',
    },
  ];

  // <SideNavBar sections={sections} />

  const keywords = "Retail marketing, In-store marketing, Merchandising services, Product demos, Brand promotion, Consumer engagement, San Francisco Bay Area retail, Los Angeles merchandising, Marketing advice"

  return (
    <Router>
      <ScrollToTop />
      <ScrollToState />
      <Routes>
        <Route
          path="/"
          element={
            <div className="App">
                {/* General Meta Tags */}
                <title>ProTarget Group LLC</title>
                <meta name="description" content="Your partner in the merchandising business" />
                <meta name="keywords" content={keywords} /> {/* Comma-separated keywords */}
                <meta name="robots" content="index, follow" /> {/* Controls indexing and crawling */}
                <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" /> {/* Character set */}
                <meta name="language" content="en" /> {/* Or 'es', etc. Based on your language */}

                {/* Open Graph (Facebook, LinkedIn, etc.) */}
                <meta property="og:title" content="ProTarget Group LLC" />
                <meta property="og:description" content="Your partner in the merchandising business" />
                <meta property="og:image" content={logo} />
                <meta property="og:type" content="website" /> {/* Or 'article', 'book', etc. */}
                <meta property="og:url" content="https://protargetgroupllc.com" /> {/* Important for correct sharing */}
                <meta property="og:site_name" content="ProTarget Group" /> {/* Your site name */}
                <meta property="og:locale" content="en_US" /> {/* Language and country */}
                {/*<meta property="fb:app_id" content="YOUR_FACEBOOK_APP_ID" />  If you have a Facebook App ID */}

                {/* Canonical URL (Important for SEO) */}
                <link rel="canonical" href="https://protargetgroupllc.com" />

                {/* Viewport (Important for responsiveness) */}
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />

                {/* Author and Copyright */}
                <meta name="author" content="Cade Beckers" />
                <meta name="copyright" content="© 2023 ProTarget Group" />

              <header className={`navbar-container ${showNavbar ? 'show' : ''}`} ref={navbarRef}>
                <nav className="navbar">
                  <div className="logo-container">
                    <img className="logo" src={logo} alt="Logo" />
                  </div>
                  <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <li>
                      <button className="no-hover" onClick={() => window.location.href = '/services'}>
                        {language === 'en' ? 'Services' : 'Servicios'}
                      </button>
                    </li>
                    <li>
                      <button className="no-hover" onClick={() => setLanguage('en')}>English</button>
                    </li>
                    <li>
                      <button className="no-hover" onClick={() => setLanguage('es')}>Español</button>
                    </li>
                  </ul>
                  <div className="hamburger" onClick={toggleMenu}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </nav>
              </header>
              <div className="nav-border" />

              <div className="language-selector">
                <button className="select-hover" onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}>
                  <div className="language-info">
                    {language === 'en' ? 'Es' : 'En'}
                    {language === 'en' ? (
                      <img src={spanish} className="language-icon" alt="Spanish" loading="lazy" />
                    ) : (
                      <img src={english} className="language-icon" alt="English" loading="lazy" />
                    )}
                  </div>
                </button>
              </div>

              <div id="home"></div>
              <div className="top-border" />
              <HomeAnimations />

              <BackgroundImage src={gradientImage} imageHeight="50vh">
                <HomepageHeader items={companyInfo} />

                <div className="background-gray">
                  <div className="content-container">
                    <div id="about" className="about main-animate">
                      <Content
                        title={language === 'en' ? 'Who Is ProTarget Group?' : '¿Quién es ProTarget Group?'}
                        text={
                          language === 'en'
                            ? `As a woman-owned business rooted in the San Francisco Bay Area, ProTarget Group LLC has spent more than twelve years 
                            mastering the retail landscape. We specialize in forging meaningful connections between brands and consumers through strategic 
                            marketing and deep consumer insights. Our collaborative approach combines data-driven strategies with personalized solutions, 
                            helping businesses adapt and thrive across various retail sectors. We pride ourselves on being agile and forward-thinking, anticipating 
                            tomorrow's market needs while delivering results today. Partner with us to transform your brand's potential into remarkable growth.`
                            : `Como empresa fundada por una mujer con sede en el Área de la Bahía de San Francisco, ProTarget Group LLC lleva más de doce años
                            dominando el panorama minorista. Nos especializamos en forjar conexiones significativas entre marcas y consumidores a través del 
                            marketing estratégico y un profundo conocimiento al consumidor. Nuestro enfoque colaborativo combina estrategias basadas en datos
                            con soluciones personalizadas, ayudando a las empresas a adaptarse y prosperar en diversos sectores minoristas. Nos enorgullecemos 
                            de ser ágiles y con visión de futuro, anticipándonos a las necesidades del mercado del mañana y ofreciendo resultados hoy. Colabore 
                            con nosotros para transformar el potencial de su marca en un crecimiento notable.`
                        }
                      />
                    </div>

                    <div className="space" />
                    <div className="bubbles">
                      <Bubble bubblesData={bubblesData} />
                    </div>
                    <div className="space" />
                  </div>
                </div>

                <div className="background-white">
                  <div className="services" id="what-you-can-expect"/>
                  <div className="content-container">
                    <div className="service">
                      <Services 
                        title={language === 'en' ? 'What You Can Expect' : 'Lo que puede esperar'} 
                        items={contentItems} 
                        dataPoints={dataPoints} 
                        servicesTitle={servicesTitle} 
                      />
                    </div>

                    <div className="space" />
                    <div className="partial-border" />

                    <div id="customers" className="content-container">
                      <div className="successStories">
                        <Suspense fallback={null}>
                          <SuccessStories title={customerTitle} />
                        </Suspense>
                        <div className="space" />
                      </div>
                    </div>

                    <div className="partial-border" />
                    <div className="contact-form"></div>
                    <div id="contact-form" className="space" />

                    <div className="contact-form main-animate">
                      <Form items={formInfo} />
                    </div>
                  </div>
                </div>

                <div id="contact-section" className="contact-section">
                  <div className="footer">
                    <Footer info={contactSection} />
                  </div>
                </div>
              </BackgroundImage>
            </div>
          }
        />
        <Route path="/services" element={<Suspense fallback={null}><ServicesPage /></Suspense>} />
        <Route path="/temp" element={<Suspense fallback={null}><Messages /></Suspense>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
